import { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import authProvider from '../authProvider';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDialog = ({
  open,
  onClose,
}: ChangePasswordDialogProps) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { translate } = useTranslation();
  const { notify } = useNotification();

  const handleSubmit = async () => {
    try {
      setError(null);
      setLoading(true);

      // Validate passwords
      if (!currentPassword || !newPassword || !confirmPassword) {
        setError(translate('pos.auth.new_password_required'));
        return;
      }

      if (newPassword !== confirmPassword) {
        setError(translate('pos.auth.new_passwords_do_not_match'));
        return;
      }

      if (newPassword.length < 8) {
        setError(translate('pos.auth.new_password_min_length'));
        return;
      }

      // Change password
      await authProvider.auth.changePassword(currentPassword, newPassword);
      notify('pos.auth.password_changed_successfully', { type: 'success' });
      handleClose();
    } catch (error) {
      console.error('Error changing password:', error);
      setError(translate('pos.auth.current_password_incorrect'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{translate('pos.auth.change_password')}</DialogTitle>
          <DialogDescription>
            {translate('pos.auth.change_password_description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="current-password">
              {translate('pos.auth.current_password')}
            </Label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">
              {translate('pos.auth.new_password')}
            </Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              {translate('pos.auth.new_password_min_length')}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">
              {translate('pos.auth.confirm_new_password')}
            </Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {translate('pos.common.action.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {translate('pos.auth.change_password')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
